import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, fmtDate, waLink, altaMsg } from "@/lib/api";
import { useCentro } from "@/lib/centro";
import { PageTitle } from "@/components/Badges";
import { PrintButton, PrintHeader } from "@/components/PrintList";
import { DateField } from "@/components/Fields";
import { ItemsEditor } from "@/components/ItemsEditor";
import { AgendarRevisionButton } from "@/components/AgendarRevision";
import { Check } from "lucide-react";

const FechaAlta = ({ a, onDone }) => {
  const set = async (fecha) => {
    if (!fecha) return;
    await api.post(`/altas/${a.id}/fecha`, { fecha });
    toast.success("Fecha de alta guardada: se han creado sus 6 citas");
    onDone();
  };
  return (
    <span className="flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase text-amber-700" data-testid={`alta-sin-fecha-${a.id}`}>Sin fecha</span>
      <input type="date" onChange={(e) => set(e.target.value)} data-testid={`alta-fecha-input-${a.id}`} className="h-6 rounded border border-amber-300 bg-amber-50 px-1 text-[11px]" />
    </span>
  );
};

const Chip = ({ a, r, i, onDone }) => {
  const [edit, setEdit] = useState(false);
  const mover = async (fecha) => {
    if (!fecha || fecha === r.fecha) return setEdit(false);
    try {
      const { data } = await api.post(`/altas/${a.id}/revisiones/${r.id}/fecha`, { fecha });
      toast.success(data.movidas ? `Cita movida ${data.dias > 0 ? "+" : ""}${data.dias} días y ${data.movidas} siguientes también` : "Cita movida");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "No se pudo mover");
    }
    setEdit(false);
  };
  const toggle = async (e) => {
    e.stopPropagation();
    const revisiones = a.revisiones.map((x) => (x.id === r.id ? { ...x, realizada: !x.realizada } : x));
    await api.put(`/altas/${a.id}`, { ...a, revisiones });
    toast.success(r.realizada ? "Cita desmarcada" : "Cita marcada como hecha");
    onDone();
  };
  const vencida = !r.realizada && (r.no_vino || (r.fecha && r.fecha < new Date().toISOString().slice(0, 10)));
  const row = r.realizada ? "bg-emerald-50/60 border-emerald-200" : vencida ? "bg-rose-50 border-rose-200" : "bg-white border-slate-200";
  return (
    <div data-testid={`alta-chip-${a.id}-${i}`} onClick={(e) => e.stopPropagation()} className={`flex items-center gap-2 rounded-md border px-2 py-1 ${row}`}>
      <button type="button" onClick={toggle} data-testid={`alta-check-${a.id}-${i}`} title="Marcar como hecha"
        className={`h-5 w-5 shrink-0 rounded grid place-items-center border transition-colors ${r.realizada ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 bg-white hover:border-emerald-500"}`}>
        {r.realizada && <Check className="h-3.5 w-3.5" />}
      </button>
      <span className={`flex-1 text-[11px] whitespace-nowrap ${r.realizada ? "text-emerald-800 line-through" : vencida ? "text-rose-700 font-semibold" : "text-slate-700"}`}>{i + 1}ª · {r.etiqueta}{r.no_vino && !r.realizada && <span data-testid={`alta-novino-${a.id}-${i}`} className="ml-1.5 rounded bg-rose-600 px-1 py-0.5 text-[9px] font-semibold uppercase text-white no-underline">No vino</span>}</span>
      {edit ? (
        <input type="date" autoFocus defaultValue={r.fecha} onBlur={(e) => mover(e.target.value)} onKeyDown={(e) => e.key === "Enter" && mover(e.target.value)}
          data-testid={`alta-chip-input-${a.id}-${i}`} className="h-6 rounded border border-slate-300 px-1 text-[11px]" />
      ) : (
        <button type="button" title="Pulsa para cambiar la fecha" onClick={() => r.id && setEdit(true)} data-testid={`alta-fecha-btn-${a.id}-${i}`}
          className="rounded px-1 text-[11px] font-[JetBrains_Mono] whitespace-nowrap text-slate-600 hover:bg-slate-100">{fmtDate(r.fecha)}</button>
      )}
    </div>
  );
};

const Citas = ({ a, onDone }) => (
  <div className="flex flex-col gap-1" data-testid={`alta-citas-${a.id}`}>
    {[...a.revisiones].sort((x, y) => (x.fecha || "9").localeCompare(y.fecha || "9")).map((r, i) => <Chip key={r.id || i} a={a} r={r} i={i} onDone={onDone} />)}
  </div>
);

const DarCita = ({ a, onDone }) => {
  const pend = [...a.revisiones].filter((x) => !x.realizada && x.fecha).sort((x, y) => x.fecha.localeCompare(y.fecha));
  const r = pend.find((x) => x.no_vino) || pend[0];
  if (!r) return null;
  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {a.patient.telefono && <a href={waLink(a.patient.telefono, altaMsg(a.patient, r))} target="_blank" rel="noreferrer" data-testid={`alta-wa-${a.id}`} className="rounded bg-[#25D366] px-2 py-1 text-[11px] font-medium text-white">WhatsApp</a>}
      <AgendarRevisionButton patient={a.patient} fecha={r.fecha} titulo={`Alta · ${r.etiqueta}`} extra={{ alta_id: a.id, alta_rev_id: r.id }} label="Dar cita" testId={`alta-cita-${a.id}`} onDone={onDone} />
    </div>
  );
};

const ESTADOS = [
  { id: "en_seguimiento", label: "En seguimiento" },
  { id: "finalizado", label: "Finalizado" },
  { id: "abandono", label: "Abandono" },
];

const AltaDialog = ({ alta, onClose, onSaved }) => {
  const [f, setF] = useState(alta);
  useEffect(() => {
    setF(alta);
  }, [alta]);
  if (!f) return null;
  const save = async () => {
    await api.put(`/altas/${f.id}`, f);
    toast.success("Protocolo guardado");
    onClose();
    onSaved();
  };
  return (
    <Dialog open={!!alta} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="alta-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">Protocolo de alta · {f.patient.nombre}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <DateField label="Fecha de alta" value={f.fecha_alta} onChange={(v) => setF({ ...f, fecha_alta: v })} testId="alta-fecha" />
          <label className="block">
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Estado</span>
            <select data-testid="alta-estado" className="field-select" value={f.estado} onChange={(e) => setF({ ...f, estado: e.target.value })}>
              {ESTADOS.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
            </select>
          </label>
        </div>
        <div>
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Revisiones</span>
          <ItemsEditor items={f.revisiones} onChange={(v) => setF({ ...f, revisiones: v })} prefix="rev" addLabel="Añadir revisión" />
        </div>
        <Textarea data-testid="alta-notas" placeholder="Notas" rows={3} value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button data-testid="alta-save" onClick={save} className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AltaLote = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const filas = txt.split("\n").map((l) => l.split(/[;\t]/).map((x) => x.trim())).filter((c) => c[0]?.length > 1)
    .map(([nombre, telefono = "", centro = "", fecha_alta = ""]) => ({ nombre, telefono, centro, fecha_alta }));
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/altas/lote", { filas });
      toast.success(`${data.creados.length} pacientes nuevos y ${data.existentes.length} que ya existían, con su protocolo de alta`);
      if (data.dudosos?.length) toast.warning(`No cargados, hay varios parecidos: ${data.dudosos.join(" · ")}`, { duration: 20000 });
      if (data.errores.length) toast.error(data.errores.join(" · "), { duration: 8000 });
      setOpen(false);
      setTxt("");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "No se pudo importar");
    }
    setBusy(false);
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="alta-lote-btn">Añadir altas en lote</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="alta-lote-dialog">
          <DialogHeader><DialogTitle className="font-[Outfit]">Añadir pacientes de alta</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Una línea por paciente: <b>Nombre; teléfono; centro; fecha de alta</b> (dd/mm/aaaa). Si el paciente ya existe (aunque esté escrito «apellidos, nombre»), solo se le crea el protocolo. Sin fecha, queda «sin fecha» hasta que la pongáis.</p>
          <Textarea rows={10} value={txt} onChange={(e) => setTxt(e.target.value)} data-testid="alta-lote-text" placeholder={"GARCIA LOPEZ, LUCIA; 600111222; MADRID; 15/09/2026"} className="font-[JetBrains_Mono] text-xs" />
          <div className="flex items-center justify-end gap-2">
            <span className="mr-auto text-xs text-slate-500" data-testid="alta-lote-count">{filas.length} pacientes</span>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button disabled={!filas.length || busy} onClick={send} data-testid="alta-lote-send" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">{busy ? "Creando…" : "Crear"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function Altas() {
  const { centro } = useCentro();
  const [estado, setEstado] = useState("en_seguimiento");
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const [soloVencidas, setSoloVencidas] = useState(false);
  const nVenc = rows?.filter((a) => a.vencida).length || 0;
  const lista = soloVencidas ? rows?.filter((a) => a.vencida) : rows;
  const load = useCallback(() => {
    api.get("/altas", { params: { centro, estado } }).then((r) => setRows(r.data));
  }, [centro, estado]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="altas-page">
      <div className="no-print"><PageTitle title="Protocolos de alta" subtitle="6 citas tras el alta: 1, 3, 6, 9, 15 y 27 meses (la última con informe final)."><AltaLote onDone={load} /><PrintButton /></PageTitle></div>
      <PrintHeader title="Protocolos de alta" detalle={[...ESTADOS, { id: "", label: "Todos" }].find((e) => e.id === estado)?.label} n={rows?.length} />
      <div className="no-print flex gap-2 mb-4">
        {[...ESTADOS, { id: "", label: "Todos" }].map((e) => (
          <button key={e.id || "all"} data-testid={`alta-tab-${e.id || "todos"}`} onClick={() => setEstado(e.id)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${estado === e.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{e.label}</button>
        ))}
        <button data-testid="alta-filtro-vencidas" onClick={() => setSoloVencidas(!soloVencidas)}
          className={`ml-auto rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${soloVencidas ? "bg-rose-600 text-white border-rose-600" : "bg-rose-50 text-rose-700 border-rose-200 hover:border-rose-400"}`}>
          {soloVencidas ? "Ver todos" : `Solo vencidas o no vino (${nVenc})`}
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="altas-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>{["Paciente", "Centro", "Fecha alta", "Revisiones", "Las citas", "Próxima revisión", "Dar cita", "Notas"].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows === null && <tr><td colSpan={8} className="p-6 text-center text-slate-400">Cargando…</td></tr>}
            {lista?.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-400" data-testid="altas-empty">{soloVencidas ? "No hay citas de alta vencidas." : "No hay protocolos en este estado."}</td></tr>}
            {lista?.map((a) => (
              <tr key={a.id} data-testid={`alta-row-${a.id}`} onClick={() => setSel(a)} className="hover:bg-slate-50 cursor-pointer">
                <td className="px-3 py-2.5 font-medium"><Link to={`/pacientes/${a.patient.id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">{a.patient.nombre}</Link></td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{a.patient.centro || "—"}</td>
                <td className="px-3 py-2.5 text-xs" onClick={(e) => !a.fecha_alta && e.stopPropagation()}>{a.fecha_alta ? fmtDate(a.fecha_alta) : <FechaAlta a={a} onDone={load} />}</td>
                <td className="px-3 py-2.5 font-[JetBrains_Mono] text-xs">{a.revisiones.filter((r) => r.realizada).length}/{a.revisiones.length}</td>
                <td className="px-3 py-2.5 min-w-[280px]"><Citas a={a} onDone={load} /></td>
                <td className={`px-3 py-2.5 text-xs whitespace-nowrap ${a.vencida ? "text-rose-600 font-semibold" : ""}`}>{fmtDate(a.proxima_revision)}{a.no_vino ? " · no vino" : a.vencida && " · vencida"}</td>
                <td className="px-3 py-2.5"><DarCita a={a} onDone={load} /></td>
                <td className="px-3 py-2.5 text-xs text-slate-500 max-w-[260px] truncate">{a.notas}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AltaDialog alta={sel} onClose={() => setSel(null)} onSaved={load} />
    </div>
  );
}
